// Castintech dashboard — Genel Bakış (admin/CRM cockpit) — Gemini mockup'una SADIK, GERÇEK veri.
// Tranche 3.2b (2026-05-31): KPI trend+sparkline (issue_throughput_7d) + Görev Akışı alan grafiği
// + Müşteri Pipeline (avatar+pill+süre) + Son Kararlar timeline + Aktif Toplantı video kartı.
// Avatarlar /characters/<ajan>.png. Sahte veri yok — trend/sparkline gerçek 7g seriden.
import { state } from '../state.js';
import { apiGetJson, apiFetch } from '../api.js';
import { escHtml, escAttr } from '../utils.js';
import { renderLoading, renderError } from '../ui/state-display.js';
import type { CouncilDecision } from './agent-council.js';
import {
  classifyHermesAuthorityStatus,
  aggregateStatus,
  isHermesAuthorityDecision,
  CATEGORY_LABEL,
  STATUS_LABEL,
} from '../lib/hermes-authority.js';
import {
  isRouteRequest,
  routeRequestLifecycle,
  routeRequestFields,
  LIFECYCLE_LABEL,
  LIFECYCLE_TONE,
} from '../lib/hermes-route-request-ui.js';

interface TPoint { date: string; total: number; done: number; blocked: number; todo: number; in_progress: number; }
interface OverviewCards { customerOps?: number; pendingApprovals?: number; riskyActions?: number; runningAgents?: number; }
interface Customer { client_slug: string; current_state: string; owner_agent?: string; entered_at?: string; }
interface Decision { date?: string; title?: string; decision_class?: string; owner_agent?: string; }
interface Meeting { title?: string; status?: string; type?: string; participant_count?: number; initiated_by?: string; actual_start?: string; }

type VS = { kind: 'loading' } | { kind: 'error'; message: string }
  | { kind: 'ready'; cards: OverviewCards; series: TPoint[]; issueCount: number; customers: Customer[]; decisions: Decision[]; meetings: Meeting[]; efqm: number | null; councilDecisions: CouncilDecision[] };
let vs: VS = { kind: 'loading' };

async function sf<T>(ep: string, pick: (j: any) => T, fb: T): Promise<T> {
  try { const r = await apiFetch(ep); if (!r.ok) return fb; return pick(await r.json()); } catch { return fb; }
}

// EFQM skoru — gerçek API gelene kadar best-effort (yoksa kart gizlenir, asla uydurma).
function pickEfqm(j: any): number | null {
  const v = j?.efqm?.overall ?? j?.efqm_overall ?? j?.overall ?? j?.score ?? j?.efqm;
  return typeof v === 'number' && v > 0 ? v : null;
}

export async function loadGenelBakis(): Promise<void> {
  vs = { kind: 'loading' };
  const [ov, rh, customers, decisions, meetings, efqm, council] = await Promise.all([
    apiGetJson<{ ok: true; cards: OverviewCards }>('/api/v2/central-brain/overview').then(r => (r.ok ? r.data.cards : {})).catch(() => ({})),
    sf<{ series: TPoint[]; issueCount: number }>('/castintech-api/runtime/health', j => ({ series: j.issue_throughput_7d || [], issueCount: j.issue_count || 0 }), { series: [], issueCount: 0 }),
    sf<Customer[]>('/castintech-api/customer-lifecycle/all', j => j.customers || [], []),
    sf<Decision[]>('/castintech-api/decisions/all', j => j.decisions || [], []),
    sf<Meeting[]>('/castintech-api/meetings', j => j.meetings || [], []),
    sf<number | null>('/castintech-api/efqm/overview', pickEfqm, null),
    // Stream 6 — Council decisions for Hermes authority status card
    sf<CouncilDecision[]>('/api/v2/council/decisions?limit=50', j => j.decisions || [], []),
  ]);
  vs = { kind: 'ready', cards: ov as OverviewCards, series: rh.series, issueCount: rh.issueCount, customers, decisions, meetings, efqm, councilDecisions: council };
}

export function renderGenelBakis(): void {
  const root = document.getElementById('view-genel-bakis');
  if (!root) return;
  if (vs.kind === 'loading') { renderLoading(root, { text: 'Genel bakış yükleniyor…' }); return; }
  if (vs.kind === 'error') { renderError(root, vs.message, { onRetry: () => { void loadGenelBakis().then(renderGenelBakis); } }); return; }
  root.innerHTML = html(vs);
}

function n(v: number | undefined): number { return typeof v === 'number' ? v : 0; }
function last<T>(a: T[]): T | undefined { return a[a.length - 1]; }

// Mini sparkline (gerçek seriden) — normalize edilmiş polyline.
function spark(vals: number[], color: string): string {
  if (vals.length < 2) return '';
  const w = 96, h = 30, mn = Math.min(...vals), mx = Math.max(...vals), rng = mx - mn || 1;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - mn) / rng) * (h - 4) - 2}`).join(' ');
  return `<svg class="gb-spark-svg" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none"><polyline points="${pts}" fill="none" stroke="${color}" stroke-width="2" stroke-linejoin="round"/></svg>`;
}
function trend(vals: number[]): string {
  if (vals.length < 2) return '';
  const f = vals[0] || 1, l = vals[vals.length - 1], pct = Math.round(((l - f) / (Math.abs(f) || 1)) * 100);
  if (pct === 0) return '';
  const up = pct > 0;
  return `<span class="gb-trend ${up ? 'up' : 'down'}">${up ? '▲' : '▼'} ${up ? '+' : ''}${pct}%</span>`;
}

function kpi(icon: string, label: string, value: number | string, gem: string, series?: number[]): string {
  return `<div class="gb-kpi" style="--gb-accent:${gem}">
    <div class="gb-kpi-top"><span class="gb-kpi-icon">${icon}</span>${series ? trend(series) : ''}</div>
    <div class="gb-kpi-mid"><div><div class="gb-kpi-value">${value}</div><div class="gb-kpi-label">${escHtml(label)}</div></div>
    ${series ? `<div class="gb-kpi-spark">${spark(series, gem)}</div>` : ''}</div>
  </div>`;
}

// Satır 2 — solda renkli ikon-kare + büyük değer (mockup'taki tile stili). extra = progress bar gibi ek.
function kpiTile(icon: string, label: string, value: number | string, gem: string, extra = ''): string {
  return `<div class="gb-tile" style="--gb-accent:${gem}">
    <div class="gb-tile-icon">${icon}</div>
    <div class="gb-tile-body"><div class="gb-tile-value">${value}</div><div class="gb-tile-label">${escHtml(label)}</div>${extra}</div>
  </div>`;
}

function efqmTile(score: number): string {
  const pct = Math.max(0, Math.min(100, (score / 10) * 100));
  const bar = `<div class="gb-tile-bar"><span style="width:${pct}%"></span></div>`;
  return `<div class="gb-tile gb-tile-wide" style="--gb-accent:var(--gem-emerald,#34d399)">
    <div class="gb-tile-icon">🏆</div>
    <div class="gb-tile-body"><div class="gb-tile-value">${score.toFixed(1)}<span class="gb-tile-unit">/10</span></div><div class="gb-tile-label">EFQM Mükemmellik</div>${bar}</div>
  </div>`;
}

// Görev Akışı alan grafiği (gerçek 7g seri).
function areaChart(series: TPoint[]): string {
  const W = 640, H = 220, pad = { l: 34, r: 12, t: 14, b: 26 };
  if (series.length < 2) return `<div class="gb-card gb-chart-wide"><div class="gb-card-title">Görev Akışı</div><div class="gb-empty-sm">Veri yok</div></div>`;
  const vals = series.map(s => s.total), mx = Math.max(...vals, 10);
  const iw = W - pad.l - pad.r, ih = H - pad.t - pad.b;
  const xs = (i: number) => pad.l + (i / (series.length - 1)) * iw;
  const ys = (v: number) => pad.t + ih - (v / mx) * ih;
  const line = series.map((s, i) => `${xs(i)},${ys(s.total)}`).join(' ');
  const area = `${pad.l},${pad.t + ih} ${line} ${xs(series.length - 1)},${pad.t + ih}`;
  const peakI = vals.indexOf(Math.max(...vals));
  const yticks = [0, Math.round(mx / 2), mx].map(v => `<text x="${pad.l - 6}" y="${ys(v) + 4}" class="gb-ax">${v}</text><line x1="${pad.l}" y1="${ys(v)}" x2="${W - pad.r}" y2="${ys(v)}" class="gb-grid"/>`).join('');
  const xlabels = series.map((s, i) => (i % 2 === 0 || i === series.length - 1) ? `<text x="${xs(i)}" y="${H - 6}" class="gb-ax" text-anchor="middle">${(s.date || '').slice(5)}</text>` : '').join('');
  return `<div class="gb-card gb-chart-wide"><div class="gb-card-title">Görev Akışı <span class="gb-card-sub">son 7 gün</span></div>
    <svg viewBox="0 0 ${W} ${H}" class="gb-area"><defs><linearGradient id="gbfill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="var(--gem-emerald,#34d399)" stop-opacity="0.35"/><stop offset="100%" stop-color="var(--gem-emerald,#34d399)" stop-opacity="0"/></linearGradient></defs>
      ${yticks}<polygon points="${area}" fill="url(#gbfill)"/><polyline points="${line}" fill="none" stroke="var(--gem-emerald,#34d399)" stroke-width="2.5"/>
      <circle cx="${xs(peakI)}" cy="${ys(vals[peakI])}" r="4" fill="var(--gem-emerald,#34d399)" stroke="#fff" stroke-width="1.5"/>
      <g transform="translate(${Math.min(xs(peakI), W - 120)},${ys(vals[peakI]) - 30})"><rect width="112" height="22" rx="5" class="gb-tip"/><text x="8" y="15" class="gb-tip-t">${(series[peakI].date || '').slice(5)}: ${vals[peakI]} görev</text></g>
      ${xlabels}</svg></div>`;
}

function avatar(slug: string | undefined, gem: string): string {
  const s = (slug || '').toLowerCase();
  return s ? `<img class="gb-av" src="/characters/face/${escAttr(s)}.png?v=3" loading="lazy" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'"/><span class="gb-av gb-av-fb" style="display:none;background:${gem}">${escHtml((s[0] || '?').toUpperCase())}</span>` : '';
}

const TONE: Record<string, string> = { prospect: 'sapphire', aday: 'sapphire', nitelikli: 'sapphire', qualified: 'sapphire', teklif: 'amber', proposal: 'amber', onboarding: 'amber', sozlesmeli: 'emerald', contracted: 'emerald', aktif: 'emerald', active: 'emerald', risk: 'ruby', 'at-risk': 'ruby', 'kayip-riski': 'ruby', sonlandi: 'muted', churned: 'muted', paused: 'muted' };
function pill(s: string): string { const t = TONE[(s || '').toLowerCase().replace(/[ _]/g, '-')] || 'sapphire'; return `<span class="gb-pill gb-pill-${t}">${escHtml(s || '—')}</span>`; }
function daysSince(d?: string): string { if (!d) return '—'; const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000); return days >= 0 ? `${days}g` : '—'; }

function pipeline(customers: Customer[]): string {
  const rows = customers.slice(0, 6).map(c => `<tr>
    <td class="gb-c-name">${escHtml(c.client_slug || '—')}</td><td>${pill(c.current_state)}</td>
    <td><span class="gb-av-cell">${avatar(c.owner_agent, 'var(--gem-sapphire,#60a5fa)')}<span class="gb-c-owner">${escHtml(c.owner_agent || '—')}</span></span></td>
    <td class="gb-c-dur">${daysSince(c.entered_at)}</td></tr>`).join('');
  return `<div class="gb-card"><div class="gb-card-title">Müşteri Pipeline <span class="gb-card-sub">${customers.length} müşteri</span></div>
    ${customers.length ? `<table class="gb-table"><thead><tr><th>Müşteri</th><th>Durum</th><th>Sorumlu Ajan</th><th>Süre</th></tr></thead><tbody>${rows}</tbody></table>` : '<div class="gb-empty-sm">Müşteri yok</div>'}</div>`;
}
const TAG_TONE: Record<string, string> = { finans: 'amber', teknoloji: 'sapphire', operasyon: 'emerald', stratejik: 'ruby', kapsam: 'sapphire' };
function decisions(ds: Decision[]): string {
  const items = ds.slice(0, 5).map(d => { const dt = d.date ? new Date(d.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) : ''; const tag = (d.decision_class || '').toLowerCase(); const tone = TAG_TONE[tag] || 'sapphire';
    return `<div class="gb-tl-item"><div class="gb-tl-dot" style="background:var(--gem-${tone},#60a5fa);box-shadow:0 0 8px var(--gem-${tone},#60a5fa)"></div><div><div class="gb-tl-date">${escHtml(dt)}</div><div class="gb-tl-title">${escHtml(d.title || '—')}</div>${d.decision_class ? `<span class="gb-tag gb-tag-${tone}">${escHtml(d.decision_class)}</span>` : ''}</div></div>`; }).join('');
  return `<div class="gb-card"><div class="gb-card-title">Son Kararlar</div>${ds.length ? `<div class="gb-timeline">${items}</div>` : '<div class="gb-empty-sm">Karar yok</div>'}</div>`;
}
function meeting(ms: Meeting[]): string {
  const m = ms.find(x => (x.status || '').toLowerCase().match(/active|ongoing|scheduled|devam/)) || ms[0];
  if (!m) return `<div class="gb-card"><div class="gb-card-title">Aktif Toplantı</div><div class="gb-empty-sm">Aktif toplantı yok</div></div>`;
  const parts = (state.agents || []).slice(0, 3).map(a => avatar(a.id, 'var(--gem-sapphire,#60a5fa)')).join('');
  let timer = ''; if (m.actual_start) { const mins = Math.floor((Date.now() - new Date(m.actual_start).getTime()) / 60000); if (mins >= 0 && mins < 6000) timer = ` · ${Math.floor(mins / 60)}s ${mins % 60}dk`; }
  return `<div class="gb-card gb-meeting"><div class="gb-card-title">Aktif Toplantı</div>
    <div class="gb-meeting-stage"><div class="gb-meeting-avs">${parts}</div><div class="gb-meeting-cam">🎥</div></div>
    <div class="gb-meeting-title">${escHtml(m.title || m.type || 'Toplantı')}</div>
    <div class="gb-meeting-meta">${escHtml((m.status || '').toUpperCase())}${timer} · ${n(m.participant_count)} katılımcı</div>
    <button class="gb-meeting-btn">🎥 Katıl (Video)</button></div>`;
}

function html(s: Extract<VS, { kind: 'ready' }>): string {
  const c = s.cards, ser = s.series;
  const totals = ser.map(p => p.total), dones = ser.map(p => p.done), blocks = ser.map(p => p.blocked);
  const lp = last(ser);
  const header = `<div class="gb-header"><div><div class="gb-title">Genel Bakış</div><div class="gb-subtitle">Castintech ekosistem kokpiti — canlı özet</div></div></div>`;
  // Satır 1 — sparkline'lı kartlar (gerçek seri olanlarda trend+spark).
  const row1 = `<div class="gb-kpi-grid gb-kpi-row1">
    ${kpi('🤖', 'Çalışan ajan', n(c.runningAgents), 'var(--gem-emerald,#34d399)')}
    ${kpi('🎫', 'Açık görev', s.issueCount, 'var(--gem-sapphire,#60a5fa)', totals)}
    ${kpi('✅', 'Tamamlanan (gün)', lp ? lp.done : 0, 'var(--gem-emerald,#34d399)', dones)}
    ${kpi('👥', 'Müşteri', s.customers.length, 'var(--gem-sapphire,#60a5fa)')}
  </div>`;
  // Satır 2 — ikon-kare tile kartları (EFQM skoru gerçekse progress bar'lı, yoksa Karar kartı).
  const efqmCard = s.efqm != null ? efqmTile(s.efqm) : kpiTile('🗳️', 'Karar', s.decisions.length, 'var(--gem-teal,#2dd4bf)');
  const row2kpi = `<div class="gb-kpi-grid gb-kpi-row2">
    ${kpiTile('⏳', 'Bekleyen onay', n(c.pendingApprovals), 'var(--gem-amber,#fbbf24)')}
    ${kpiTile('🚫', 'Bloklu', lp ? lp.blocked : 0, 'var(--gem-ruby,#f87171)')}
    ${efqmCard}
    ${kpiTile('⚠️', 'Riskli aksiyon', n(c.riskyActions), 'var(--gem-ruby,#f87171)')}
  </div>`;
  const kpis = `${row1}${row2kpi}`;
  const row2 = `<div class="gb-row2">${areaChart(ser)}${agentDonut()}</div>`;
  const row3 = `<div class="gb-row3">${pipeline(s.customers)}${decisions(s.decisions)}${meeting(s.meetings)}</div>`;
  // Governance / Agent Council özet kartı slot'u — render'ı governance-summary.ts
  // sahipleniyor; loadGovernanceSummary + renderGovernanceSummary main.ts'te
  // genel-bakis navigation'a bağlı. EFQM Kriteri 4. Read-only.
  const governanceSlot = '<div id="gb-governance-card"></div>';
  // Stream 6 — Hermes Authority Status kartı: 3 kategori satırı + aggregate top-line.
  // Hermes propose-only — council onayı = governance yetkisi (DB-grant yok).
  const hermesCard = renderHermesAuthorityCard(s.councilDecisions);
  return `${header}${kpis}${governanceSlot}${hermesCard}${row2}${row3}`;
}

function statusColor(status: string): string {
  if (status === 'council_approved') return '#9be8a8';
  if (status === 'rejected') return '#f0a8a8';
  if (status === 'submitted_open') return '#fbbf24';
  return 'var(--muted)';
}

function renderHermesAuthorityCard(decisions: CouncilDecision[]): string {
  const matched = decisions.filter(isHermesAuthorityDecision);
  if (matched.length === 0) {
    return ''; // hiç Hermes kararı yoksa kart gösterilmez
  }
  const perCategory = classifyHermesAuthorityStatus(decisions);
  const agg = aggregateStatus(perCategory);
  const aggLabel = STATUS_LABEL[agg];
  const aggColor = statusColor(agg) === 'var(--muted)' ? '#94a3b8' : statusColor(agg);
  const rows = perCategory.map(c => {
    const label = STATUS_LABEL[c.status];
    const cat = CATEGORY_LABEL[c.category];
    return `<div style="display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid var(--border,#1f2937);font-size:13px;">
      <span style="color:var(--text);">🛡️ ${escHtml(cat)}</span>
      <span style="color:${statusColor(c.status)};">${escHtml(label)}</span>
    </div>`;
  }).join('');
  // Honest not — Hermes propose-only doktrini: council onayı = yetki, DB-grant yok.
  const note = `<div style="margin-top:8px;padding:8px;background:var(--bg2,#0f1419);border-left:3px solid #34d399;font-size:12px;color:var(--muted);border-radius:4px;">Hermes propose-only (dış operatör) — council onayı governance yetkisidir. Teknik enforcement bridge tarafında; <code>principal_permission_grants</code> DB-grant gerekmez/uygulanmaz. Bkz. [[decisions/2026-06-14-hermes-operator-authority-grant]].</div>`;
  return `<div class="rt-card" style="margin:12px 0;">
    <div class="rt-card-title" style="display:flex;justify-content:space-between;align-items:center;">
      <span>🛡️ Hermes Yetki Durumu</span>
      <span style="color:${aggColor};font-size:13px;font-weight:500;">${escHtml(aggLabel)}</span>
    </div>
    ${rows}
    ${renderLastRouteRequest(decisions)}
    ${note}
  </div>`;
}

// Son Hermes → Nexus route-request surface (en yeni hermes.route_request kararı).
function renderLastRouteRequest(decisions: CouncilDecision[]): string {
  const rrs = decisions.filter(isRouteRequest)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  if (rrs.length === 0) return '';
  const d = rrs[0];
  const f = routeRequestFields(d);
  const lc = routeRequestLifecycle(d);
  return `<div style="margin-top:10px;padding:8px;background:var(--bg2,#0f1419);border-left:3px solid #2563eb;border-radius:4px;font-size:12px;">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
      <span style="color:var(--text);font-weight:500;">🛰️ Son Hermes → ${escHtml(f.targetAgent)} route-request</span>
      <span style="color:${LIFECYCLE_TONE[lc]};">${escHtml(LIFECYCLE_LABEL[lc])}</span>
    </div>
    <div style="color:var(--muted);">${escHtml(f.requestedAction.slice(0, 200) || '—')}</div>
    ${f.correlationId ? `<div style="color:var(--muted);font-size:11px;margin-top:2px;">corr: <code>${escHtml(f.correlationId)}</code> · owner: nexus · ${escHtml(d.createdAt)}</div>` : ''}
  </div>`;
}

function agentDonut(): string {
  const agents = state.agents || [];
  const b: Record<string, number> = { active: 0, busy: 0, idle: 0, error: 0 };
  for (const a of agents) { const s = (a.status || 'idle').toLowerCase(); (s in b) ? b[s]++ : b.idle++; }
  const total = agents.length || 1;
  const segs: Array<[string, number, string]> = [['Aktif', b.active, 'var(--gem-emerald,#34d399)'], ['Meşgul', b.busy, 'var(--gem-amber,#fbbf24)'], ['Boşta', b.idle, 'var(--gem-sapphire,#60a5fa)'], ['Hata', b.error, 'var(--gem-ruby,#f87171)']];
  const C = 2 * Math.PI * 52; let off = 0;
  const arcs = segs.map(([, v, col]) => { const len = (v / total) * C; const s = v > 0 ? `<circle r="52" cx="70" cy="70" fill="none" stroke="${col}" stroke-width="16" stroke-dasharray="${len} ${C - len}" stroke-dashoffset="${-off}" transform="rotate(-90 70 70)"></circle>` : ''; off += len; return s; }).join('');
  const legend = segs.map(([l, v, col]) => `<div class="gb-legend-row"><span class="gb-dot" style="background:${col}"></span>${l}<span class="gb-legend-val">${v}</span></div>`).join('');
  return `<div class="gb-card gb-chart-card"><div class="gb-card-title">Ajan Durumu <span class="gb-card-sub">${agents.length} ajan</span></div><div class="gb-donut-wrap"><svg viewBox="0 0 140 140" class="gb-donut"><circle r="52" cx="70" cy="70" fill="none" stroke="var(--border)" stroke-width="16"></circle>${arcs}<text x="70" y="66" text-anchor="middle" class="gb-donut-num">${agents.length}</text><text x="70" y="83" text-anchor="middle" class="gb-donut-cap">Toplam Ajan</text></svg><div class="gb-legend">${legend}</div></div></div>`;
}
